import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  CircularProgress,
  IconButton,
  Link,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { useLazyGetUserExamsQuery } from "@/store/api/user/examApi";
import { useSelector } from "react-redux";
import { selectIsAdmin } from "@/store/slices/authSlice";
import { useLazyGetAdminExamsQuery } from "@/store/api/adminApi";
import { ExamItem } from "@/components/user/ExamItem";
import { useSidebar } from "@/contexts/SidebarContext";
import { useLazyGetStudentExercisesQuery } from "@/store/api/user/exerciseApi";
import { MenuBook as MenuBookIcon } from "@mui/icons-material";
import { useLazyGetAdminExercisesQuery } from "@/store/api/admin/exerciseApi";
import { RichTextDisplay } from "@/components/common";
import {
  CourseSection,
  FeedbackSection,
  RankingSidebar,
  HeroBannerSection,
  PublicMockTestSection,
} from "@/components/homepage";

const Home = () => {
  const { currentUser } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const isAdmin = useSelector(selectIsAdmin);
  const { expandSidebar, collapseSidebar } = useSidebar();

  const [getAdminExams, { data: adminData, isFetching: adminLoading }] =
    useLazyGetAdminExamsQuery();
  const [getUserExams, { data: userData, isLoading: userLoading }] =
    useLazyGetUserExamsQuery();
  const [
    getStudentExercises,
    { data: studentExerciseData, isLoading: studentExerciseLoading },
  ] = useLazyGetStudentExercisesQuery();
  const [
    getAdminExercises,
    { data: adminExerciseData, isLoading: adminExerciseLoading },
  ] = useLazyGetAdminExercisesQuery();

  const exerciseData = useMemo(
    () => (isAdmin ? adminExerciseData : studentExerciseData),
    [isAdmin, adminExerciseData, studentExerciseData]
  );
  const exerciseLoading = useMemo(
    () => (isAdmin ? adminExerciseLoading : studentExerciseLoading),
    [isAdmin, adminExerciseLoading, studentExerciseLoading]
  );

  // const data = isAdmin ? adminData : userData;
  const examsLoading = isAdmin ? adminLoading : userLoading;
  const limit = 3;
  const exerciseLimit = 2;

  const [page, setPage] = useState(1);
  const [exercisePage, setExercisePage] = useState(1);

  const exams = useMemo(() => {
    return isAdmin ? adminData?.data : userData?.data;
  }, [isAdmin, adminData, userData]);

  const pagination = useMemo(() => {
    return isAdmin ? adminData?.pagination : userData?.pagination;
  }, [isAdmin, adminData, userData]);

  const exercises = useMemo(() => {
    return exerciseData?.data || [];
  }, [exerciseData]);

  const exercisePagination = useMemo(() => {
    return exerciseData?.pagination;
  }, [exerciseData]);

  useEffect(() => {
    expandSidebar();
    return () => {
      collapseSidebar();
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    if (isAdmin) {
      getAdminExams({ page, limit, sort: "title", order: "asc" });
    } else {
      getUserExams({ page, limit, sort: "title", order: "asc" });
    }
  }, [page, currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    if (!isAdmin) {
      getStudentExercises({ page: exercisePage, limit: exerciseLimit });
    } else {
      getAdminExercises({ page: exercisePage, limit: exerciseLimit });
    }
  }, [exercisePage, isAdmin, currentUser]);

  const handlePrevPage = () => {
    setPage(page - 1);
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  const handlePrevExercisePage = () => {
    setExercisePage(exercisePage - 1);
  };

  const handleNextExercisePage = () => {
    setExercisePage(exercisePage + 1);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #e3f2fd 100%)",
        py: 0,
        width: "100%",
        px: 0,
      }}
    >
      <Container maxWidth="xl" sx={{ width: "100%", pt: 3 }}>
        {/* Main Layout: Content + Ranking Sidebar */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {/* Left: Main Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Hero Banner Section */}
            <HeroBannerSection />
            {/* Course Ads Section */}
            <CourseSection />
            {/* Student Feedback Section */}
            <FeedbackSection />
            {/* Public Mock Tests — free practice, visible to guests */}
            <PublicMockTestSection />
          </Box>

          {/* Right: Ranking Sidebar - only show when logged in */}
          {currentUser && (
            <Box
              sx={{
                width: { xs: "100%", lg: 320 },
                flexShrink: 0,
              }}
            >
              <RankingSidebar />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
